import { Component, EventEmitter, Input, Output, inject, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BooleanType, RecommendationStateEnum } from '@coin/shared/core/util-enums';
import { SuccessionManagementHelper, TinyHelpers } from '@coin/shared/core/util-helpers';
import { EnumToTextPipe } from '@coin/shared/core/util-pipes';
import {
  ACTIVITY_STREAM_LIST_ITEM_UI_MAPPING,
  ACTIVITY_STREAM_SUBTITLE_ITEM_TYPE_UI_MAPPING,
  ACTIVITY_STREAM_SUBTITLE_EVENT_TYPE_UI_MAPPING,
  ACTIVITY_STREAM_TITLE_UI_MAPPING,
  ActivityStreamChangedProperty,
  ActivityStreamEventOperation,
  ActivityStreamHelper,
  ActivityStreamItem,
  ActivityStreamItemEventType,
  ActivityStreamItemType,
  ACTIVITY_STREAM_ITEM_LINK_EVENT_TYPE_UI_MAPPING
} from '@coin/shared/activity-stream/util';

@Component({
  selector: 'coin-c-activity-stream-item',
  templateUrl: './activity-stream-item.component.html',
  styleUrls: ['./activity-stream-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ActivityStreamItemComponent {
  private enumToTextPipe = inject(EnumToTextPipe);
  private translate = inject(TranslateService);

  public pascalcaseToKebabCase = TinyHelpers.pascalcaseToKebabCase;
  public getColorForRecommendationState = SuccessionManagementHelper.getColorForRecommendationState;

  @Input() item: ActivityStreamItem;
  @Output() showDetailsClicked = new EventEmitter<ActivityStreamItem>();

  public activityStreamItemType = ActivityStreamItemType;

  public get itemChangedProperties(): ActivityStreamChangedProperty[] {
    let changedProperties = this.item?.changedProperties;
    if (changedProperties) {
      const itemType = this.item?.itemType;

      if (itemType === ActivityStreamItemType.InquiryApprovalStep) {
        changedProperties = this.filterInquiryApprovalProcessStepChanges(changedProperties);
      } else if (itemType === ActivityStreamItemType.InquiryApprovalProposal) {
        changedProperties = this.filterInquiryApprovalProcessProposalChanges(changedProperties);
      }
    }
    return changedProperties;
  }

  public get itemChangedProperty(): ActivityStreamChangedProperty {
    return this.itemChangedProperties?.[0];
  }

  public get activityTypeInfo(): Record<string, string> {
    return ACTIVITY_STREAM_LIST_ITEM_UI_MAPPING[this.item?.itemType];
  }

  public get hasMultipleChangedProperties(): boolean {
    return this.item?.changedProperties?.length > 1;
  }

  public get showEmployeePreview(): boolean {
    return (
      this.item?.eventType === ActivityStreamItemEventType.PositionRequest ||
      this.item?.eventType === ActivityStreamItemEventType.PositionRequestApproval
    );
  }

  public get isRecommendation(): boolean {
    const types = [
      ActivityStreamItemType.NewRecommendationAdded,
      ActivityStreamItemType.TalentRemovedFromPool,
      ActivityStreamItemType.StateChanged,
      ActivityStreamItemType.RevertedRecommendation,
      ActivityStreamItemType.NewTalentAddedToPool
    ];
    return types.includes(this.item?.itemType);
  }

  public get recommendationState(): RecommendationStateEnum {
    const recommendationState = this.item?.changedProperties?.find(
      property => property.propertyName === 'RecommendationState'
    );
    return recommendationState?.currentValue as RecommendationStateEnum;
  }

  public get talentPoolReferenceTrackTitle(): string {
    const talentPoolTrack = this.item?.changedProperties?.find(property => property.propertyName === 'ReferenceTrack');
    return talentPoolTrack?.currentValue as string;
  }

  public get itemTitle(): string {
    if (this.item?.itemType === ActivityStreamItemType.InquiryApprovalStep)
      return this.getInquiryApprovalProcessStepName();
    let itemTitle =
      ACTIVITY_STREAM_TITLE_UI_MAPPING[this.item?.eventType] || this.enumToTextPipe.transform(this.item?.itemType);

    if (itemTitle?.endsWith('Changed') && this.item?.eventOperation === ActivityStreamEventOperation.Created) {
      itemTitle = itemTitle.replace('Changed', 'Added');
    }

    return itemTitle;
  }

  public get itemSubtitle(): string {
    if (this.item?.itemType === ActivityStreamItemType.InquiryApprovalStep) {
      const inquiryApprovalProcessStepSubtitle = this.getInquiryApprovalProcessStateName();
      if (inquiryApprovalProcessStepSubtitle?.length > 0) return inquiryApprovalProcessStepSubtitle;
    }

    const isDoneProperty = this.item?.changedProperties?.find(property => property.propertyName === 'IsDone');

    if (isDoneProperty?.originalValue === BooleanType.False && isDoneProperty?.currentValue === BooleanType.True) {
      return 'activity-stream.allocation.finalized';
    }

    if (isDoneProperty?.originalValue === BooleanType.True && isDoneProperty?.currentValue === BooleanType.False) {
      return 'activity-stream.allocation.reopened';
    }

    if (
      this.item?.changedProperties?.some(property =>
        ['MeritSettingsChanged', 'EquitySettingsChanged', 'EquitySubSettingsChanged'].includes(property.propertyName)
      )
    ) {
      return this.enumToTextPipe.transform(this.itemChangedProperty?.propertyName);
    }

    if (this.item?.metadata) {
      return `${this.translate.instant('activity-stream.affected')}: ${this.item.metadata.count}`;
    }

    const mappingFn =
      ACTIVITY_STREAM_SUBTITLE_ITEM_TYPE_UI_MAPPING[this.item?.itemType] ||
      ACTIVITY_STREAM_SUBTITLE_EVENT_TYPE_UI_MAPPING[this.item?.eventType];
    if (mappingFn) {
      return mappingFn(this.item);
    }

    return this.hasMultipleChangedProperties
      ? ''
      : this.enumToTextPipe.transform(this.itemChangedProperty?.propertyName);
  }

  public get itemLink(): string | null {
    const mappingFn = ACTIVITY_STREAM_ITEM_LINK_EVENT_TYPE_UI_MAPPING[this.item?.eventType];
    return mappingFn ? mappingFn(this.item) : undefined;
  }

  private filterInquiryApprovalProcessStepChanges(
    changedProperties: ActivityStreamChangedProperty[]
  ): ActivityStreamChangedProperty[] {
    return changedProperties
      .filter(changedProperty => changedProperty.propertyName !== 'StepName')
      .map(changedProperty => {
        if (changedProperty.propertyName === 'State') {
          const translationkeyPrefix = 'activity-stream.inquiry-approval-process.states';
          const originalValue = changedProperty?.originalValue
            ? `${translationkeyPrefix}.${this.pascalcaseToKebabCase(changedProperty.originalValue as string)}`
            : changedProperty?.originalValue;
          const currentValue = changedProperty?.currentValue
            ? `${translationkeyPrefix}.${this.pascalcaseToKebabCase(changedProperty.currentValue as string)}`
            : changedProperty?.currentValue;

          return {
            ...changedProperty,
            ...{
              originalValue,
              currentValue
            }
          };
        }

        return changedProperty;
      });
  }

  private filterInquiryApprovalProcessProposalChanges(
    changedProperties: ActivityStreamChangedProperty[]
  ): ActivityStreamChangedProperty[] {
    return changedProperties.map(changedProperty => {
      if (changedProperty.propertyName === 'State') {
        const translationkeyPrefix = 'activity-stream.inquiry-approval-process.states';
        const originalValue = changedProperty?.originalValue
          ? `${translationkeyPrefix}.${this.pascalcaseToKebabCase(changedProperty.originalValue as string)}`
          : changedProperty?.originalValue;
        const currentValue = changedProperty?.currentValue
          ? `${translationkeyPrefix}.${this.pascalcaseToKebabCase(changedProperty.currentValue as string)}`
          : changedProperty?.currentValue;

        return {
          ...changedProperty,
          ...{
            originalValue,
            currentValue
          }
        };
      }

      return changedProperty;
    });
  }

  private getInquiryApprovalProcessStepName(): string {
    const stepNameProperty = this.item?.changedProperties?.find(property => property.propertyName === 'StepName');
    const stepName = (stepNameProperty?.originalValue ?? stepNameProperty?.currentValue) as string;
    const eventTypeTranslationKeyPart = this.pascalcaseToKebabCase(this.item.eventType);

    return `activity-stream.inquiry-approval-process.steps.${eventTypeTranslationKeyPart}.${stepName}`;
  }
  private getInquiryApprovalProcessStateName(): string {
    const stateProperty = this.item?.changedProperties?.find(property => property.propertyName === 'State');
    const state = stateProperty?.currentValue as string;

    if (state) return `activity-stream.inquiry-approval-process.states.${this.pascalcaseToKebabCase(state)}`;
  }

  public onShowClicked(): void {
    this.showDetailsClicked.emit(this.item);
  }

  public isDisplayedUnformatted(property: ActivityStreamChangedProperty): boolean {
    return ActivityStreamHelper.isDisplayedUnformatted(property);
  }

  public isHtmlBlock(item: ActivityStreamItem, property: ActivityStreamChangedProperty): boolean {
    return ActivityStreamHelper.isHtmlBlock(item, property);
  }
}
